import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AddIcon from '@mui/icons-material/Add';
import AbsenceRequestModal from '../hrComponents/AbsenceRequestModal';

interface Balance {
  typeId: number;
  typeName: string;
  isDefault: boolean;
  displayUnit: string;
  entitlementDays: number | null;
  remainingDays: number | null;
  entitlementHours: number | null;
  remainingHours: number | null;
}

interface LeaveRequest {
  id: number;
  typeId: number;
  startDate: string;
  endDate: string;
  hours: number;
  days: number;
  status: string;
  category: string;           // leave | sickness | lateness
  minutesLate: number | null;
}

const ROW_H = 31;

const STATUS_LABEL: Record<string, string> = { leave: 'Leave', sick: 'Sick', late: 'Late' };

const fmtDay = (d: string, withMonth = true) =>
  new Date(d).toLocaleDateString('en-GB', withMonth ? { day: 'numeric', month: 'short' } : { day: 'numeric' });

// "13–14 Nov", "28 Nov – 2 Dec", "18 Dec"
const fmtRange = (start: string, end: string) => {
  if (start === end) return fmtDay(start);
  const s = new Date(start), e = new Date(end);
  return s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()
    ? `${fmtDay(start, false)}–${fmtDay(end)}`
    : `${fmtDay(start)} – ${fmtDay(end)}`;
};

const trim = (n: number) => String(+n.toFixed(2));

const LeaveCompactCard: React.FC<{ auth: boolean }> = ({ auth }) => {
  const [balances, setBalances] = useState<Balance[]>([]);
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(async () => {
    if (!auth) return;
    const [balRes, reqRes] = await Promise.allSettled([
      axios.get('hr-absence/my-balance'),
      axios.get('hr-absence/my'),
    ]);
    if (balRes.status === 'fulfilled') setBalances(Array.isArray(balRes.value.data) ? balRes.value.data : []);
    if (reqRes.status === 'fulfilled') setRequests(Array.isArray(reqRes.value.data) ? reqRes.value.data : []);
  }, [auth]);

  useEffect(() => { load(); }, [load]);

  // How many rows fit into the free height of the list (slot height differs per row layout / screen)
  const listRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState(3);
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const rowH = (el.firstElementChild as HTMLElement | null)?.offsetHeight || ROW_H;
      setFit(Math.max(1, Math.floor(el.clientHeight / rowH)));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const main = balances.find(b => b.isDefault) ?? balances[0];
  const inHours = main?.displayUnit === 'hours';
  const left = main ? (inHours ? main.remainingHours : main.remainingDays) : null;
  const total = main ? (inHours ? main.entitlementHours : main.entitlementDays) : null;
  const pct = left !== null && total ? Math.max(0, Math.min(100, (left / total) * 100)) : 0;

  // All absences: upcoming first (nearest), then the latest past records; as many as fit.
  // Chip = category only: a leave request shows as Leave right away (approved or not);
  // rejected/cancelled ones drop out of the list.
  const today = new Date().toISOString().slice(0, 10);
  const live = requests.filter(r => ['pending', 'approved', 'recorded'].includes(r.status));
  const chip = (r: LeaveRequest) =>
    r.category === 'sickness' ? 'sick' : r.category === 'lateness' ? 'late' : 'leave';
  const future = live
    .filter(r => r.endDate >= today)
    .sort((a, b) => a.startDate.localeCompare(b.startDate))
    .map(r => ({ ...r, chip: chip(r) }));
  const past = live
    .filter(r => r.endDate < today)
    .sort((a, b) => b.startDate.localeCompare(a.startDate))
    .map(r => ({ ...r, chip: chip(r) }));
  const rows = [...future, ...past].slice(0, fit);
  const amount = (r: LeaveRequest) =>
    r.category === 'lateness' ? `${r.minutesLate ?? 0}m` : inHours ? `${trim(r.hours)}h` : `${trim(r.days)}d`;

  return (
    <div className="dashboard-card lv-card">
      <div className="lv-head">
        <div className="lv-ic"><CalendarMonthOutlinedIcon sx={{ fontSize: 15 }} /></div>
        <span className="lv-title">Absence</span>
        <div style={{ flex: 1 }} />
        <button type="button" className="lv-add" onClick={() => setModalOpen(true)}>
          <AddIcon sx={{ fontSize: 14 }} /><span className="lv-add-t">Request</span>
        </button>
      </div>

      {main && (
        <>
          <div className="lv-hero">
            <span className="lv-num">{left === null ? '∞' : trim(left)}</span>
            {total !== null && left !== null && (
              <span className="lv-of">of {trim(total)} {inHours ? 'hours' : 'days'} left</span>
            )}
            {/* the balance and bar refer to this leave type, not to all absences */}
            <span className="lv-type">{main.typeName}</span>
          </div>
          {total !== null && left !== null && (
            <div className="lv-bar"><i style={{ width: `${pct}%` }} /></div>
          )}
        </>
      )}


      <div className="lv-list" ref={listRef}>
        {rows.length === 0 && <div className="lv-empty">No absences yet</div>}
        {rows.map(r => (
          <div key={r.id} className="lv-row">
            <span className={`lv-chip ${r.chip}`}>{STATUS_LABEL[r.chip]}</span>
            <span className="lv-dates">{fmtRange(r.startDate, r.endDate)}</span>
            <span className="lv-val">{amount(r)}</span>
          </div>
        ))}
      </div>

      <Link to="/hr" className="lv-link">View all <ArrowForwardIcon sx={{ fontSize: 12 }} /></Link>

      <AbsenceRequestModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={load} selfMode />
    </div>
  );
};

export default LeaveCompactCard;
